import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { Trophy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTranslation } from '@/components/utils/translations';

// Australia data
const winnersDataAustralia = [
  { vendor: "Respondus Inc.", deals: 3, years: "2025", clients: "Curtin, ACU, Bond", category: "e-Exam / Assessment" },
  { vendor: "Echo360 (EchoVideo Cloud)", deals: 2, years: "2024–25", clients: "ANU, Curtin", category: "Video Capture" },
  { vendor: "Panopto Inc.", deals: 2, years: "2025", clients: "Bond, CSU", category: "Video Capture" },
  { vendor: "Poll Everywhere", deals: 2, years: "2024–25", clients: "UON, Curtin", category: "Classroom Engagement" },
  { vendor: "Inspera AS", deals: 1, years: "2025", clients: "ANU", category: "e-Exam" },
  { vendor: "Cadmus", deals: 1, years: "2025", clients: "ANU", category: "Assessment" },
  { vendor: "Instructure (Canvas)", deals: 1, years: "2025", clients: "ACU", category: "LMS" },
  { vendor: "Kaltura Inc.", deals: 1, years: "2025", clients: "CDU", category: "Video Capture" },
  { vendor: "Mentimeter", deals: 1, years: "2025", clients: "Curtin", category: "Polling" },
  { vendor: "Microsoft (Copilot)", deals: 1, years: "2024", clients: "ANU", category: "AI / Productivity" },
  { vendor: "Career Central (Target Connect)", deals: 1, years: "2024", clients: "ANU", category: "Career Services" },
  { vendor: "Transact (Mobile Credential)", deals: 1, years: "2024", clients: "USyd", category: "Campus ID" },
  { vendor: "Explorance (Blue)", deals: 1, years: "2024", clients: "UTS", category: "Teaching Evaluation" },
  { vendor: "Brickfield (Accessibility Toolkit)", deals: 1, years: "2024", clients: "UOW", category: "Accessibility" },
  { vendor: "Cayuse (Research Suite)", deals: 1, years: "2025", clients: "UoM", category: "Research Admin" },
  { vendor: "Rimini Street (ERP Support)", deals: 1, years: "2025", clients: "UoM", category: "ERP" },
  { vendor: "My eQuals", deals: 1, years: "2025", clients: "ANU", category: "Digital Credentials" }
];

// Hong Kong data
const winnersDataHongKong2024 = [
  { vendor: "Ex Libris (Clarivate)", deals: 2, years: "2024", clients: "CityU, HKBU", category: "Library AI / Discovery" },
  { vendor: "FeedbackFruits", deals: 2, years: "2024", clients: "CityU, HKBU", category: "Assessment Automation" },
  { vendor: "Okta", deals: 1, years: "2024", clients: "CityU", category: "Identity & Access Management" },
  { vendor: "Zoom", deals: 1, years: "2024", clients: "CUHK", category: "Collaboration" },
  { vendor: "Microsoft (Copilot for Enterprise)", deals: 1, years: "2024", clients: "HKBU", category: "AI / Productivity" }
];

const winnersDataHongKong2025 = [
  { vendor: "Ex Libris (Clarivate)", deals: 2, years: "2025", clients: "HKU, SFU", category: "Library AI / Discovery" },
  { vendor: "Turnitin (incl. Gradescope)", deals: 2, years: "2025", clients: "HSUHK, EdUHK", category: "Assessment Automation" },
  { vendor: "Elsevier (Pure)", deals: 1, years: "2025", clients: "HKU", category: "Research Information (CRIS)" }
];

// United Kingdom data
const winnersDataUK2024 = [
  { vendor: "Explorance (Blue)", deals: 3, years: "2024", clients: "Cardiff, Aston, Cumbria", category: "Course & Teaching Evaluation" },
  { vendor: "Symplicity", deals: 2, years: "2024", clients: "Aberdeen (MyCareerHub, Advocate)", category: "Career Services / Case Mgmt" },
  { vendor: "Fortinet", deals: 1, years: "2024", clients: "Aberdeen", category: "Cybersecurity" },
  { vendor: "Secure Nexus", deals: 1, years: "2024", clients: "Dundee", category: "Cybersecurity" },
  { vendor: "FUTR AI", deals: 1, years: "2024", clients: "Aberdeen", category: "AI Chatbot" },
  { vendor: "Virtual Dissection Table", deals: 1, years: "2024", clients: "Aberdeen", category: "Virtual Labs" },
  { vendor: "Corporate Project Solutions", deals: 1, years: "2024", clients: "Aston", category: "Student Portal (Microsoft)" },
  { vendor: "Unibuddy", deals: 1, years: "2024", clients: "Brunel", category: "Student Engagement" },
  { vendor: "Questionmark (OnDemand)", deals: 1, years: "2024", clients: "Edinburgh", category: "Online Assessment" },
  { vendor: "QS (Admissions CRM)", deals: 1, years: "2024", clients: "Edinburgh", category: "Admissions CRM" },
  { vendor: "Wooclap", deals: 1, years: "2024", clients: "Edinburgh", category: "Classroom Engagement" },
  { vendor: "Electric Paper (EvaSys)", deals: 1, years: "2024", clients: "Cumbria", category: "Course Evaluation" },
  { vendor: "Elsevier (Pure)", deals: 1, years: "2024", clients: "Cumbria", category: "Research CRIS" },
  { vendor: "Softcat (ITSM)", deals: 1, years: "2024", clients: "Cumbria", category: "ITSM" },
  { vendor: "Panopto", deals: 1, years: "2024", clients: "UCA", category: "Lecture Capture" }
];

const winnersDataUK2025 = [
  { vendor: "Panopto", deals: 3, years: "2025", clients: "Bristol, UCA (2 licences)", category: "Lecture Capture / Video CMS" },
  { vendor: "EasyTransfer", deals: 2, years: "2025", clients: "Durham, LBU", category: "Payments / Financial Aid" },
  { vendor: "Flywire", deals: 1, years: "2025", clients: "Edinburgh", category: "International Payments" },
  { vendor: "Kinetic (Kx Cloud)", deals: 1, years: "2025", clients: "Edinburgh", category: "Student Housing" },
  { vendor: "WSO2 (Choreo)", deals: 1, years: "2025", clients: "Edinburgh", category: "Integration Platform" },
  { vendor: "Elastic UK", deals: 1, years: "2025", clients: "Bedfordshire", category: "Integration / Data" },
  { vendor: "Phoenix Software Ltd", deals: 1, years: "2025", clients: "Bedfordshire", category: "Cybersecurity / Vulnerability" },
  { vendor: "FeedbackFruits", deals: 1, years: "2025", clients: "Durham", category: "Peer Learning" },
  { vendor: "Vevox", deals: 1, years: "2025", clients: "Aberdeen", category: "Classroom Polling" },
  { vendor: "Worktribe (Ethics)", deals: 1, years: "2025", clients: "Leeds Beckett", category: "Research Ethics" },
  { vendor: "Explorance (Blue + MLY)", deals: 1, years: "2025", clients: "West London", category: "Course Evaluation" },
  { vendor: "Time2Eat", deals: 1, years: "2025", clients: "Brunel", category: "Campus Dining" }
];

// South Africa data
const winnersDataSouthAfrica2024 = [
  { vendor: "Converis", deals: 2, years: "2024", clients: "WSU, DUT", category: "CRIS" },
  { vendor: "Flywire", deals: 2, years: "2024", clients: "RU, UNISA", category: "Student Payments" },
  { vendor: "Anthology", deals: 4, years: "2024", clients: "NWU (CRM), TUT (Engage), UP (Reach, Milestone)", category: "CRM / Student Success" },
  { vendor: "Ex Libris", deals: 2, years: "2024", clients: "SMU, UFS (Alma + Primo)", category: "Library Systems" },
  { vendor: "Elsevier Pure", deals: 1, years: "2024", clients: "UJ", category: "CRIS/RIMS" },
  { vendor: "Mimecast", deals: 1, years: "2024", clients: "WSU", category: "Cybersecurity" },
  { vendor: "Grammarly", deals: 1, years: "2024", clients: "WSU", category: "AI/Writing" },
  { vendor: "Invigilator App", deals: 1, years: "2024", clients: "WSU", category: "Remote Proctoring" },
  { vendor: "Explorance Blue", deals: 1, years: "2024", clients: "SU", category: "Course Evaluations" },
  { vendor: "Parchment CeCredentialTrust", deals: 1, years: "2024", clients: "SU", category: "Digital Certificates" },
  { vendor: "FeedbackFruits", deals: 1, years: "2024", clients: "RU", category: "Peer Learning" },
  { vendor: "MoUJi", deals: 1, years: "2024", clients: "UJ", category: "AI Chatbot" },
  { vendor: "Slate", deals: 1, years: "2024", clients: "UCT-GSB", category: "Student Recruitment CRM" }
];

const winnersDataSouthAfrica2025 = [
  { vendor: "Ex Libris", deals: 4, years: "2025", clients: "UNIZULU (Primo, Rapido, Rialto, Primo RA)", category: "Library Systems" },
  { vendor: "Anthology", deals: 2, years: "2025", clients: "NWU (Student SIS)", category: "SIS" },
  { vendor: "Flywire", deals: 1, years: "2025", clients: "UKZN", category: "Student Payments" },
  { vendor: "PayGate (DPO)", deals: 1, years: "2025", clients: "DUT", category: "Student Payments" },
  { vendor: "Adumo", deals: 1, years: "2025", clients: "CUT", category: "Student Payments" },
  { vendor: "StarRez", deals: 1, years: "2025", clients: "NWU", category: "Housing" },
  { vendor: "Panopto", deals: 1, years: "2025", clients: "CUT", category: "Video CMS" },
  { vendor: "Figshare", deals: 2, years: "2025", clients: "UFH, UL", category: "Research Data" },
  { vendor: "Inspera", deals: 1, years: "2025", clients: "NMU", category: "e-Assessment/Proctoring" },
  { vendor: "Wooclap", deals: 1, years: "2025", clients: "RU", category: "Classroom Engagement" },
  { vendor: "EXCALIBAR", deals: 1, years: "2025", clients: "WSU", category: "Student Debt" },
  { vendor: "Mimecast", deals: 1, years: "2025", clients: "WSU (expansion)", category: "Cybersecurity" }
];

const commentaryAustralia = "Respondus leads with 3 new e-exam/assessment wins in 2025. Video capture remains hot with Echo360, Panopto, and Kaltura all gaining logos. Canvas competes in LMS migrations. Assessment modernization and cloud video dominate 2024-25 market activity.";

const commentaryHongKong = "Ex Libris dominates with 4 total wins across both years via Primo RA AI overlays, showing libraries lead digital innovation. FeedbackFruits and Turnitin split assessment automation wins—both following pilot-to-production paths. Elsevier Pure signals HKU's research modernization. Pattern: incumbents win add-on modules; new entrants land via department-led pilots, not central IT RFPs.";

const commentaryUK2024 = "Explorance Blue won 3 course evaluation deals (Cardiff, Aston, Cumbria), making it 2024's most successful vendor. Symplicity added 2 wins at Aberdeen (career services + case management). Cybersecurity vendors Fortinet and Secure Nexus each landed one institution. Total 17 new vendor adoptions across teaching, security, and infrastructure categories—no single vendor dominated beyond Explorance.";

const commentaryUK2025 = "Panopto led 2025 with 3 lecture capture wins (Bristol, UCA × 2). EasyTransfer secured 2 international payment deals (Durham, LBU). 10 other vendors gained single logos each. 2025's 15 total adoptions spread across payments, integrations, video CMS, and niche upgrades—no dominant player emerged, reflecting a market of targeted, department-led purchases rather than institution-wide platform swaps.";

const commentaryUKCombined = "Overall 2024–2025: Explorance Blue and Panopto led with 3 wins each across both years. EasyTransfer and Symplicity secured 2 deals each. 32 total new vendor adoptions—most frequent categories were Course Evaluation (4), Lecture Capture (4), Cybersecurity (3), and Payments (3). Pattern: targeted, department-level purchases rather than institution-wide platform swaps.";

const commentarySouthAfrica2024 = "2024 winners: Anthology led with 4 deals across CRM and student success platforms (NWU, TUT, UP). Converis, Ex Libris, and Flywire each secured 2 wins. Key categories: Research management (CRIS), CRM modernization, library systems, and cybersecurity dominated the year.";

const commentarySouthAfrica2025 = "2025 winners: Ex Libris dominated with 4 library ecosystem wins at UNIZULU (full stack adoption). Anthology secured 2 SIS deals. Figshare won 2 research data contracts (UFH, UL). Payment vendors (Flywire, PayGate, Adumo) collectively won 3 deals. Pattern: Full ecosystem replacement (Ex Libris) and payment modernization drove the market.";

const commentarySouthAfricaCombined = "Overall 2024-2025: Ex Libris and Anthology led with 6 and 6 wins respectively across both years. Flywire secured 3 payment deals. Figshare won 2 research data contracts. Total: 36 confirmed new vendor adoptions. Dominant categories: Library systems (6), CRM/SIS (6), Student payments (5), Research management (5), reflecting modernization across academic and operational infrastructure.";

const commentaryCombined = "Across Australia and Hong Kong: Assessment modernization dominates with Respondus, Turnitin, and FeedbackFruits gaining ground. Ex Libris leads library AI innovation (4 wins via Primo RA). Video capture stays competitive (Echo360, Panopto, Kaltura). Pattern: Australia shows SaaS migration scale; Hong Kong favors pilot-to-production dept-led buys over central RFPs.";

// Skillwell data (Fortune 500 enterprises)
const winnersDataSkillwell2024 = [
  { vendor: "Accenture LearnVantage", deals: 1, years: "2024", clients: "S&P Global", category: "LXP / Learning Delivery" },
  { vendor: "Workera", deals: 1, years: "2024", clients: "Accenture", category: "Skills / Talent Intelligence" },
  { vendor: "Udacity (acquired)", deals: 1, years: "2024", clients: "Accenture", category: "L&D Content (M&A)" },
  { vendor: "Award Solutions (acquired)", deals: 1, years: "2024", clients: "Accenture", category: "L&D Content (M&A)" },
  { vendor: "Fayetteville State University", deals: 1, years: "2024", clients: "Amazon Career Choice", category: "University Partner" },
  { vendor: "Shepherd University", deals: 1, years: "2024", clients: "Amazon Career Choice", category: "University Partner" },
  { vendor: "Stanford Online", deals: 1, years: "2024", clients: "Accenture", category: "Specialist Curriculum" }
];

const winnersDataSkillwell2025 = [
  { vendor: "Eightfold AI", deals: 1, years: "2025", clients: "S&P Global", category: "Talent Intelligence" },
  { vendor: "ChatGPT Enterprise (OpenAI)", deals: 1, years: "2025", clients: "Accenture", category: "GenAI Co-pilot" },
  { vendor: "TalentSprint (acquired)", deals: 1, years: "2025", clients: "Accenture", category: "L&D Content (M&A)" },
  { vendor: "Ascendient Learning (acquired)", deals: 1, years: "2025", clients: "Accenture", category: "L&D Content (M&A)" },
  { vendor: "Aidemy (acquired)", deals: 1, years: "2025", clients: "Accenture", category: "L&D Content (M&A)" },
  { vendor: "SAP Learning", deals: 1, years: "2025", clients: "Accenture", category: "Certification Partner" },
  { vendor: "University of Southern Indiana", deals: 1, years: "2025", clients: "Amazon Career Choice", category: "University Partner" },
  { vendor: "Unmanned Safety Institute", deals: 1, years: "2025", clients: "Amazon Career Choice", category: "Specialist Training" }
];

const commentarySkillwell2024 = "2024 winners: Accenture LearnVantage landed S&P Global as a flagship client. Workera won as skills-intelligence layer. First content M&A: Udacity and Award Solutions acquired by Accenture. Amazon Career Choice added two new university partners plus Stanford Online as premium curriculum partner.";
const commentarySkillwell2025 = "2025 winners: Eightfold AI won S&P Global's talent intelligence deal. ChatGPT Enterprise deployed across Accenture. M&A accelerated with three more content acquisitions (TalentSprint, Ascendient, Aidemy). SAP Learning became a major certification partner. Amazon added USI and Unmanned Safety Institute to Career Choice.";
const commentarySkillwellCombined = "Overall 2024-2025: Accenture dominated with 9 vendor relationships (1 LXP client win, 1 skills platform, 5 content acquisitions, 2 curriculum partners). Amazon Career Choice added 3 university partners and 1 specialist provider. S&P Global made 2 strategic platform buys (LearnVantage, Eightfold AI). Key trend: Move from LXP to talent intelligence and GenAI co-pilots.";

// Kairadigital data (Manufacturing/Retail/Pharma enterprises)
const winnersDataKairadigital2024 = [
  { vendor: "OMP", deals: 2, years: "2024", clients: "AstraZeneca, Nestlé", category: "Supply Chain Planning (Unison Planning APS)" }
];

const winnersDataKairadigital2025 = [
  { vendor: "NVIDIA", deals: 3, years: "2025", clients: "Unilever, L'Oréal, Nestlé", category: "AI Infrastructure & Omniverse / Digital-Twin Platforms" },
  { vendor: "Microsoft", deals: 2, years: "2025", clients: "Schneider Electric, Nestlé", category: "Industrial Copilot + Cloud for Digital-Twin Content" }
];

const commentaryKairadigital2024 = "In 2024, OMP is the only clear 'multi-account' winner in this group, anchoring decision-centric supply chain planning for both AstraZeneca and Nestlé.";
const commentaryKairadigital2025 = "NVIDIA emerges as the strongest AI / digital-twin content winner in 2025 (Unilever, L'Oréal, Nestlé). Microsoft shows up repeatedly as the copilot / cloud backbone for Schneider (Copilot for Sales + Industrial Copilot) and Nestlé (hosting part of the content-twin stack).";
const commentaryKairadigitalCombined = "Cross-year pattern (2024–2025 combined): OMP dominates planning wins (AstraZeneca, Nestlé). NVIDIA emerges as the strongest AI / digital-twin content winner in 2025 (Unilever, L'Oréal, Nestlé). Microsoft shows up repeatedly as the copilot / cloud backbone for Schneider and Nestlé.";

// Plataformaa 2024 winners
const winnersDataPlataformaa2024 = [
  { vendor: "Adobe", deals: 2, years: "2024", clients: "UNAM, UFRO", category: "Content Creation & Media Production" },
  { vendor: "Unity", deals: 1, years: "2024", clients: "Universidad Distrital FJC", category: "Simulation / AR-VR Labs" },
  { vendor: "Coursera", deals: 1, years: "2024", clients: "UdeG", category: "Workforce Skills / Micro-credentials" }
];

// Plataformaa 2025 winners (no confirmed awards)
const winnersDataPlataformaa2025 = [];

const commentaryPlataformaa2024 = "Adobe dominated 2024 with 2 content creation wins (UNAM Creative Cloud, UFRO Premiere Pro). Unity secured Universidad Distrital's simulation platform licensing. Coursera won UdeG's Career Academy deployment (250 licenses). UNAD's online assessment platform vendor remains undisclosed. Total: 4 identified vendor wins.";
const commentaryPlataformaa2025 = "No confirmed vendor awards identified in 2025 evidence. UNAL's eInnovation cloud platform + virtual assistant remains in pipeline (not yet awarded). Year appears focused on scaling 2024 investments rather than major new procurements.";
const commentaryPlataformaaCombined = "Overall 2024-2025: Adobe leads with 2 content creation wins. Unity, Coursera each secured 1 deal. 2025 produced no confirmed vendor awards yet, with UNAL's eInnovation platform still in procurement pipeline. Pattern: 2024 buying concentrated in content creation, simulation, and workforce skills; 2025 adoption phase with minimal new procurement.";

// Wayground 2024 winners
const winnersDataWayground2024 = [
  { vendor: "Google", deals: 1, years: "2024", clients: "Baja California", category: "Learning & Collaboration Platforms" },
  { vendor: "BEO Education", deals: 1, years: "2024", clients: "Nuevo León", category: "Teacher Development" },
  { vendor: "Softya Soluciones", deals: 1, years: "2024", clients: "Nuevo León", category: "Devices & Hardware" },
  { vendor: "Inter @ Enter", deals: 1, years: "2024", clients: "Nuevo León", category: "Devices & Hardware" },
  { vendor: "Telmex", deals: 1, years: "2024", clients: "Nuevo León", category: "Connectivity" },
  { vendor: "Edutest", deals: 1, years: "2024", clients: "Nuevo León", category: "Assessment & Certification" }
];

// Wayground 2025 winners
const winnersDataWayground2025 = [
  { vendor: "ITEP México", deals: 1, years: "2025", clients: "Nuevo León", category: "Assessment & Certification" },
  { vendor: "Diversi SpA", deals: 1, years: "2025", clients: "SLEP Atacama", category: "Admin & Support Platforms" }
];

const commentaryWayground2024 = "2024 winners focused on devices, connectivity, and assessment. Nuevo León emerged as the key market, awarding deals to multiple vendors across hardware (Softya, Inter @ Enter), connectivity (Telmex), assessment (Edutest), and teacher development (BEO Education). Google secured Baja California's collaborative platform licensing, marking the only major platform win of the year.";
const commentaryWayground2025 = "2025 shows fewer publicly disclosed vendor wins due to tender processes still in progress. ITEP México continues Nuevo León's focus on English assessment with 1,300 diagnostic licenses. Diversi SpA wins SLEP Atacama's special-education management platform. Major tenders from Plan Ceibal/ANEP and Jalisco are expected to generate significant vendor wins, but winners are not yet publicly disclosed.";
const commentaryWaygroundCombined = "Overall 2024-2025: Nuevo León emerged as the most active market, awarding 6 vendor contracts across devices, connectivity, assessment, and teacher development. Google (Baja California), ITEP México (Nuevo León), and Diversi SpA (SLEP Atacama) secured the only platform/software wins with publicly disclosed vendors. Major 2025 tenders from Plan Ceibal/ANEP and Jalisco remain in progress.";

// Learnwise data (academic integrity / originality tools)
const winnersDataLearnwise2024 = [
  { vendor: "Turnitin family", deals: 2, years: "2024", clients: "Mapúa (Turnitin + Draft Coach); UFES (Turnitin Similarity)", category: "Academic Integrity / Originality" },
  { vendor: "Compilatio", deals: 1, years: "2024", clients: "University of the Republic", category: "Academic Integrity / Originality" },
  { vendor: "CourseLoop", deals: 1, years: "2024", clients: "University of Technology Sydney", category: "Curriculum Management" },
  { vendor: "Alibaba Cloud", deals: 1, years: "2024", clients: "Universiti Malaya", category: "Cloud / Skills Platform" },
  { vendor: "Canvas", deals: 1, years: "2024", clients: "Miriam College", category: "LMS" }
];

const winnersDataLearnwise2025 = [
  { vendor: "Turnitin family", deals: 3, years: "2025", clients: "UASLP (iThenticate); UFES (Turnitin AI Writing); UFBA (Turnitin Similarity Add-On)", category: "Academic Integrity / Originality" },
  { vendor: "Compilatio", deals: 1, years: "2025", clients: "UTFSM", category: "Academic Integrity / Originality" },
  { vendor: "Clarivate", deals: 1, years: "2025", clients: "UCEN (IDEA UCEN)", category: "Academic Integrity / Discovery" },
  { vendor: "Plagius", deals: 1, years: "2025", clients: "UFU", category: "Plagiarism Detection" }
];

const commentaryLearnwise2024 = "2024 winners: Turnitin family led with Mapúa (Turnitin + Draft Coach) and UFES (Turnitin Similarity). Compilatio at University of the Republic. Outside integrity: CourseLoop at UTS, Alibaba Cloud at Universiti Malaya, Canvas at Miriam College.";
const commentaryLearnwise2025 = "2025 winners: Turnitin family dominated with 3 deals (UASLP iThenticate, UFES AI Writing, UFBA Similarity Add-On). Compilatio (UTFSM), Clarivate (UCEN), and Plagius (UFU) each secured one account. Turnitin and Compilatio are the only vendors that repeat across more than one account.";
const commentaryLearnwiseCombined = "The final picture is still quite concentrated. Once you strip out renewals, upgrades to older installs, and projects where the vendor is not clearly public, the number of true new deals becomes fairly small. The strongest pattern is around the Turnitin family—the clearest repeated vendor cluster, appearing in multiple forms: integrity checking, AI-writing detection, and writing-support add-ons. Compilatio is the only other vendor that repeats across more than one account. Outside academic integrity, UTS stands out with curriculum-management (CourseLoop), Miriam College moved to Canvas, Universiti Malaya launched Alibaba Cloud skills programs, and UCEN introduced Clarivate-powered discovery. The market looks active but selective: real pockets of net-new buying, spread across different categories and institutions rather than a broad wave of large-scale multi-category procurement.";

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div className="bg-[#0A1A2F] text-white p-3 rounded-lg shadow-lg max-w-sm border border-[#00C2C2]">
        <p className="font-semibold text-sm mb-1">{data.vendor}</p>
        <p className="text-xs text-[#00C2C2] mb-2">{data.deals} New Institutional Deal{data.deals > 1 ? 's' : ''}</p>
        <p className="text-xs mb-1"><span className="font-semibold">Years:</span> {data.years}</p>
        <p className="text-xs mb-1"><span className="font-semibold">New Clients:</span> {data.clients}</p>
        <p className="text-xs"><span className="font-semibold">Category:</span> {data.category}</p>
      </div>
    );
  }
  return null;
};

export default function MarketWinners({ market }) {
  const { t } = useTranslation();
  const [filter, setFilter] = useState('all');
  
  const isUK = market === 'united-kingdom';
  const isSouthAfrica = market === 'south-africa';
  const isSkillwell = market === 'skillwell';
  const isKairadigital = market === 'kairadigital';
  const isWayground = market === 'wayground';
  const isPlataformaa = market === 'plataformaa';
  const isLearnwise = market === 'learnwise';
  
  let displayWinnersData = [];
  let displayCommentary = '';
  
  if (isLearnwise) {
    if (filter === '2024') {
      displayWinnersData = winnersDataLearnwise2024;
      displayCommentary = commentaryLearnwise2024;
    } else if (filter === '2025') {
      displayWinnersData = winnersDataLearnwise2025;
      displayCommentary = commentaryLearnwise2025;
    } else {
      // Aggregated: Turnitin family 5, Compilatio 2, CourseLoop 1, Alibaba Cloud 1, Canvas 1, Clarivate 1, Plagius 1
      displayWinnersData = [
        { vendor: "Turnitin family", deals: 5, years: "2024–25", clients: "Mapúa, UFES, UASLP, UFBA (Turnitin, Draft Coach, iThenticate, AI Writing, Similarity Add-On)", category: "Academic Integrity / Originality" },
        { vendor: "Compilatio", deals: 2, years: "2024–25", clients: "University of the Republic, UTFSM", category: "Academic Integrity / Originality" },
        { vendor: "CourseLoop", deals: 1, years: "2024", clients: "UTS", category: "Curriculum Management" },
        { vendor: "Alibaba Cloud", deals: 1, years: "2024", clients: "Universiti Malaya", category: "Cloud / Skills Platform" },
        { vendor: "Canvas", deals: 1, years: "2024", clients: "Miriam College", category: "LMS" },
        { vendor: "Clarivate", deals: 1, years: "2025", clients: "UCEN", category: "Academic Integrity / Discovery" },
        { vendor: "Plagius", deals: 1, years: "2025", clients: "UFU", category: "Plagiarism Detection" }
      ];
      displayCommentary = commentaryLearnwiseCombined;
    }
  } else if (isPlataformaa) {
    if (filter === '2024') {
      displayWinnersData = winnersDataPlataformaa2024;
      displayCommentary = commentaryPlataformaa2024;
    } else if (filter === '2025') {
      displayWinnersData = winnersDataPlataformaa2025;
      displayCommentary = commentaryPlataformaa2025;
    } else { // 'all'
      displayWinnersData = [...winnersDataPlataformaa2024, ...winnersDataPlataformaa2025];
      displayCommentary = commentaryPlataformaaCombined;
    }
  } else if (isWayground) {
    if (filter === '2024') {
      displayWinnersData = winnersDataWayground2024;
      displayCommentary = commentaryWayground2024;
    } else if (filter === '2025') {
      displayWinnersData = winnersDataWayground2025;
      displayCommentary = commentaryWayground2025;
    } else { // 'all'
      displayWinnersData = [...winnersDataWayground2024, ...winnersDataWayground2025];
      displayCommentary = commentaryWaygroundCombined;
    }
  } else if (isKairadigital) {
    if (filter === '2024') {
      displayWinnersData = winnersDataKairadigital2024;
      displayCommentary = commentaryKairadigital2024;
    } else if (filter === '2025') {
      displayWinnersData = winnersDataKairadigital2025;
      displayCommentary = commentaryKairadigital2025;
    } else { // 'all'
      displayWinnersData = [...winnersDataKairadigital2024, ...winnersDataKairadigital2025];
      displayCommentary = commentaryKairadigitalCombined;
    }
  } else if (isSkillwell) {
    if (filter === '2024') {
      displayWinnersData = winnersDataSkillwell2024;
      displayCommentary = commentarySkillwell2024;
    } else if (filter === '2025') {
      displayWinnersData = winnersDataSkillwell2025;
      displayCommentary = commentarySkillwell2025;
    } else { // 'all'
      displayWinnersData = [...winnersDataSkillwell2024, ...winnersDataSkillwell2025];
      displayCommentary = commentarySkillwellCombined;
    }
  } else if (isUK) {
    if (filter === '2024') {
      displayWinnersData = winnersDataUK2024;
      displayCommentary = commentaryUK2024;
    } else if (filter === '2025') {
      displayWinnersData = winnersDataUK2025;
      displayCommentary = commentaryUK2025;
    } else { // 'all'
      displayWinnersData = [...winnersDataUK2024, ...winnersDataUK2025];
      displayCommentary = commentaryUKCombined;
    }
  } else if (isSouthAfrica) {
    if (filter === '2024') {
      displayWinnersData = winnersDataSouthAfrica2024;
      displayCommentary = commentarySouthAfrica2024;
    } else if (filter === '2025') {
      displayWinnersData = winnersDataSouthAfrica2025;
      displayCommentary = commentarySouthAfrica2025;
    } else { // 'all'
      displayWinnersData = [...winnersDataSouthAfrica2024, ...winnersDataSouthAfrica2025];
      displayCommentary = commentarySouthAfricaCombined;
    }
  } else if (market === 'hong-kong') {
    if (filter === '2024') {
      displayWinnersData = winnersDataHongKong2024;
    } else if (filter === '2025') {
      displayWinnersData = winnersDataHongKong2025;
    } else { // 'all'
      displayWinnersData = [...winnersDataHongKong2024, ...winnersDataHongKong2025];
    }
    displayCommentary = commentaryHongKong; 
  } else { // 'australia'
    if (filter === '2024') {
      displayWinnersData = winnersDataAustralia.filter(d => d.years.includes('2024'));
    } else if (filter === '2025') {
      displayWinnersData = winnersDataAustralia.filter(d => d.years.includes('2025'));
    } else { // 'all'
      displayWinnersData = winnersDataAustralia;
    }
    displayCommentary = commentaryAustralia;
  }
  
  const sortedData = [...displayWinnersData].sort((a, b) => b.deals - a.deals).slice(0, 15);

  return (
    <Card className="border-0 shadow-md">
      <CardHeader>
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <div className="flex items-center gap-2">
            <Trophy className="w-5 h-5 text-[#00C2C2]" />
            <CardTitle className="text-lg font-semibold text-[#0A1A2F]">{t('marketWinners')}</CardTitle>
          </div>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant={filter === 'all' ? 'default' : 'outline'}
              onClick={() => setFilter('all')}
              className={filter === 'all' ? 'bg-[#00C2C2] hover:bg-[#00A3A3]' : ''}
            >
              2024+2025
            </Button>
            <Button
              size="sm"
              variant={filter === '2024' ? 'default' : 'outline'}
              onClick={() => setFilter('2024')}
              className={filter === '2024' ? 'bg-[#00C2C2] hover:bg-[#00A3A3]' : ''}
            >
              2024
            </Button>
            <Button
              size="sm"
              variant={filter === '2025' ? 'default' : 'outline'}
              onClick={() => setFilter('2025')}
              className={filter === '2025' ? 'bg-[#00C2C2] hover:bg-[#00A3A3]' : ''}
            >
              2025
            </Button>
          </div>
        </div>
        <p className="text-sm text-[#5C6B7A]">{t('vendorsGaining')}</p>
        <p className="text-xs text-[#5C6B7A] mt-1">Illustrative sample — not live market intelligence for this tenant.</p>
        {isUK && (
          <p className="text-xs text-[#5C6B7A] italic mt-1">(data is based on 16 non-Canvas universities)</p>
        )}
      </CardHeader>
      <CardContent>
        <div className="h-[500px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart 
              data={sortedData} 
              layout="vertical"
              margin={{ top: 5, right: 30, left: 5, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" horizontal={false} strokeOpacity={0.2} />
              <XAxis type="number" tick={{ fontSize: 11 }} stroke="#5C6B7A" />
              <YAxis 
                type="category" 
                dataKey="vendor" 
                tick={{ fontSize: 10 }}
                stroke="#5C6B7A"
                width={180}
              />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(0, 194, 194, 0.1)' }} />
              <Bar dataKey="deals" radius={[0, 4, 4, 0]}>
                {sortedData.map((entry, index) => (
                  <Cell 
                    key={`cell-${index}`} 
                    fill={entry.deals >= 3 ? '#00C2C2' : entry.deals === 2 ? '#3D8C91' : '#89CDD4'}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        
        <div className="mt-6 md:mt-8 p-3 bg-gray-50 rounded-lg">
          <p className="text-xs font-semibold text-[#0A1A2F] mb-1">{t('marketInsights')}</p>
          <p className="text-xs text-[#5C6B7A] leading-relaxed">
            {displayCommentary}
          </p>
        </div>
      </CardContent>
    </Card>
  );
}